import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, Lock } from 'lucide-react';
import { PaymentForm } from '../components/payment/PaymentForm';
import { OTPForm } from '../components/payment/OTPForm';
import { ProcessingAnimation } from '../components/payment/ProcessingAnimation';
type PaymentStep = 'details' | 'otp' | 'processing';
export function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // In a real app, this would come from the location state or a payment intent API
  const amount = location.state?.amount || 4500;
  const orderId = location.state?.orderId || `ORD-${Math.floor(100000 + Math.random() * 900000)}`;
  const [step, setStep] = useState<PaymentStep>('details');
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [paymentData, setPaymentData] = useState<any>(null);
  const handlePaymentSubmit = (data: any) => {
    setPaymentData(data);
    setStep('otp');
  };
  const handleOTPSubmit = (otp: string) => {
    setStep('processing');
    setPaymentStatus('processing');
    // Simulate API call
    setTimeout(() => {
      setPaymentStatus('success');
    }, 5000);
  };
  const handleCancel = () => {
    navigate('/checkout');
  };
  const handlePaymentComplete = () => {
    navigate('/order-confirmation', {
      state: {
        orderId,
        amount,
        paymentMethod: 'Credit/Debit Card',
        paymentStatus: 'Paid',
        receiptNo: `REC-${Math.floor(10000000 + Math.random() * 90000000)}`,
        date: new Date().toISOString(),
        cardLast4: paymentData?.cardNumber?.slice(-4) || '****'
      }
    });
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-1478" data-mp="3gmklgstqwsyr">
      {/* Paysys Labs Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-6 shadow-sm" data-id="element-1479" data-mp="3umc456v7ixj3">
        <div className="max-w-3xl mx-auto flex justify-between items-center" data-id="element-1480" data-mp="3qki8jczvcjps">
          <div className="flex items-center space-x-2" data-id="element-1481" data-mp="1hrjog4lxj7i">
            <img src="https://cdn.magicpatterns.com/uploads/wtigZ1ySqavxJHCJbfDDio/Paysys_Logo.png" alt="Paysys Labs" className="h-8 object-contain" data-id="element-1482" data-mp="1j2kq86o4cux" />
          </div>
          <div className="flex items-center text-sm text-gray-500" data-id="element-1483" data-mp="bffd92qh8ko3">
            <Shield className="w-4 h-4 mr-1 text-green-500" data-id="element-1484" data-mp="g6fcien2jetr" />
            <span data-id="element-1485" data-mp="g7yvid0403z8">Secure Checkout</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center p-4 sm:p-6" data-id="element-1486" data-mp="ka3k2xbpaaf0">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100" data-id="element-1487" data-mp="p5iv7hibps7q">
          {/* Progress Bar */}
          <div className="h-1 w-full bg-gray-100" data-id="element-1488" data-mp="z59pbi2z257a">
            <div className="h-full bg-blue-600 transition-all duration-500 ease-in-out" style={{
            width: step === 'details' ? '33%' : step === 'otp' ? '66%' : '100%'
          }} data-id="element-1489" data-mp="z3ykrg1pctf4" />
          </div>

          <div className="p-6 sm:p-8" data-id="element-1490" data-mp="30x6azkiusvfs">
            {step === 'details' && <>
                <div className="mb-6" data-id="element-1491" data-mp="1i2bmro8cjmyc">
                  <h1 className="text-2xl font-bold text-gray-900" data-id="element-1492" data-mp="1i3cyzw13mfhn">
                    Payment Details
                  </h1>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-1493" data-mp="198yixr8qv0xh">
                    Enter your card information to complete the purchase.
                  </p>
                </div>
                <PaymentForm onSubmit={handlePaymentSubmit} onCancel={handleCancel} totalAmount={amount} data-id="element-1494" data-mp="148an8s9klvwx" />
              </>}

            {step === 'otp' && <OTPForm onSubmit={handleOTPSubmit} onCancel={handleCancel} data-id="element-1495" data-mp="1wzu9kl1grq35" />}

            {step === 'processing' && <ProcessingAnimation status={paymentStatus} onComplete={handlePaymentComplete} data-id="element-1496" data-mp="1ryn2ko75auxj" />}
          </div>

          {/* Footer inside card */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-between items-center" data-id="element-1497" data-mp="1n2zb2256xpov">
            <div className="flex items-center text-xs text-gray-500" data-id="element-1498" data-mp="blg17qx3uohc">
              <Lock className="w-3 h-3 mr-1" data-id="element-1499" data-mp="bk505yv1nutx" />
              256-bit SSL Encryption
            </div>
            <div className="flex items-center space-x-2" data-id="element-1500" data-mp="1zguq2a0dv6jr">
              <img src="https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png" alt="Visa" className="h-4 opacity-60" data-id="element-1501" data-mp="24948l4z654c0" />
              <img src="https://cdn.magicpatterns.com/uploads/qD9CTacyXTdwXXPtrCxZQw/MasterCard_Logo.png" alt="Mastercard" className="h-4 opacity-60" data-id="element-1502" data-mp="1qjalar3bas4e" />
              <img src="https://cdn.magicpatterns.com/uploads/8TcgwChg7bMUnkD6TfzVkn/UnionPay_Logo.png" alt="UnionPay" className="h-4 opacity-60" data-id="element-1503" data-mp="2j5vd1snp968h" />
              <img src="https://cdn.magicpatterns.com/uploads/u4UiZ3PWX5VH4BCbbcVNsE/PayPak_Logo.png" alt="PayPak" className="h-4 opacity-60" data-id="element-1504" data-mp="2n6xbw8dpfg9x" />
              <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-4 opacity-60" data-id="element-1505" data-mp="2e6ipt6sx1x5d" />
            </div>
          </div>
        </div>
      </main>

      {/* Paysys Labs Footer */}
      <footer className="py-6 text-center" data-id="element-1506" data-mp="12soh5awfhrzo">
        <div className="flex items-center justify-center text-sm text-gray-500" data-id="element-1507" data-mp="v8ccyynhb6pf">
          <span data-id="element-1508" data-mp="vagreaj07o3y">Powered by</span>
          <img src="https://cdn.magicpatterns.com/uploads/wtigZ1ySqavxJHCJbfDDio/Paysys_Logo.png" alt="Paysys Labs" className="h-5 object-contain ml-2" data-id="element-1509" data-mp="q9srezcsrwxl" />
        </div>
        <p className="text-xs text-gray-400 mt-2" data-id="element-1510" data-mp="146y25cnghjz6">
          © {new Date().getFullYear()} Paysys Labs. All rights reserved.
        </p>
      </footer>
    </div>;
}